import type { HeadingLevel } from '~/components/text/Heading.vue'

interface SectionTitleProps {
  level: HeadingLevel
  isSemantic: boolean
}

/**
 * Generates section heading level based on index.
 * - First section (index 0): H1
 * - All other sections: H2
 * - Falls back to div when wantsSemantic is false
 *
 * @see {@link /docs/decisions.md#section-heading-levels} for rationale
 *
 * @param {number} moduleIndex - The index of the section module (0-based)
 * @param {boolean} [hasHeading=true] - Whether this section has a heading
 * @param {boolean} [wantsSemantic=true] - Whether to render semantic heading tags
 * @returns {SectionTitleProps} Object containing level and isSemantic
 */
export default function useSectionRules(
  moduleIndex: number,
  hasHeading: boolean = true,
  wantsSemantic: boolean = true
): SectionTitleProps {
  // Determine heading level: H1 for first section, H2 for all others
  const level: HeadingLevel = moduleIndex === 0 ? 1 : 2

  // Semantic only if section has heading and wants semantic tags
  const isSemantic = hasHeading && wantsSemantic

  return { level, isSemantic }
}
